import { useCallback, useEffect, useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiIcon, MiMenu } from "@miplatform/components";
import { isRecentlyAdded } from "@miplatform/utils";
import {
  mdiArrowLeft,
  mdiArrowRight,
  mdiFileMultipleOutline,
  mdiInformationSlabCircleOutline,
  mdiLink,
  mdiPencilOutline,
} from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { IPriceLevelPayload } from "../PricingMatrix.types";
import {
  DROP_NAME_COL_WIDTH,
  PRICE_LVL_COL_WIDTH,
} from "../constants/constants";
import { ColumnHeaderActionButtons } from "./ColumnHeaderActionButtons";
import { PriceLevelInfo } from "./PriceLevelInfo";
import { ScrollButtons } from "./ScrollButtons";

export function PriceLevelHeaderRow({
  form,
  readOnly = false,
  transform,
  priceLevels,
  maxVisibleLevels,
  visibleStartIndex,
  openPriceLevelForm,
  activePriceLevelIndex,
  setMaxVisibleLevels,
  setVisibleStartIndex,
  handleEditDropPrices,
  handleEditPriceLevel,
  handleSaveDropPrices,
  handleCancelPriceLevel,
  handleReorderPriceLevels,
  handleDuplicatePriceLevel,
}: PriceLevelHeaderRowProps) {
  const [selectedPriceLevel, setSelectedPriceLevel] =
    useState<IPriceLevelPayload | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { formState: { isDirty = false } = {} } = form ?? {};
  const { classes, theme, cx } = useStyles({ transform });
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastScrollableIndex = priceLevels.length - maxVisibleLevels;
  const isPriceLevelAdded = activePriceLevelIndex !== null;

  const updateMaxVisible = useCallback(() => {
    if (scrollRef.current) {
      const windowWidth = scrollRef.current.clientWidth;
      const extendedWidth = isPriceLevelAdded ? PRICE_LVL_COL_WIDTH : 0;
      const visibleLevels = (windowWidth - extendedWidth) / PRICE_LVL_COL_WIDTH;
      setMaxVisibleLevels(Math.floor(visibleLevels));
    }
  }, [activePriceLevelIndex]);

  // left/right scroll should show when column reach max browser width
  useEffect(() => {
    const observer = new ResizeObserver(updateMaxVisible);
    if (scrollRef.current) {
      observer.observe(scrollRef.current);
    }
    return () => observer.disconnect();
  }, [updateMaxVisible]);

  // if browser width is expanded, it will auto adjust column visibility
  useEffect(() => {
    if (visibleStartIndex > lastScrollableIndex) {
      setVisibleStartIndex(Math.max(0, lastScrollableIndex));
    }
  }, [visibleStartIndex, lastScrollableIndex]);

  // auto scroll to last column after adding a new price level
  useEffect(() => {
    if (isPriceLevelAdded) {
      setVisibleStartIndex(Math.max(0, lastScrollableIndex));
    }
  }, [priceLevels.length]);

  return (
    <Box className={classes.headerRow}>
      <Box className={classes.dropNameCol} />
      <Box className={classes.scrollWrapper} ref={scrollRef}>
        <Box className={classes.scrollContent}>
          {priceLevels.map((level, idx) => {
            const isRecentlyModified = isRecentlyAdded(
              level.modifiedOn,
              5,
              "seconds"
            );
            const isActive = activePriceLevelIndex === idx;
            const hasDependents = priceLevels.some(
              (pl) => pl.parentId === level.id
            );
            const isBasedOn = level.parentId;
            const showLinkIcon = hasDependents || isBasedOn;

            return (
              <Box key={level.id} className={classes.priceLevelBox}>
                <Box
                  className={cx(
                    classes.priceLevelCol,
                    isActive
                      ? classes.priceLevelColActive
                      : isRecentlyModified && classes.recentlyModified
                  )}
                >
                  <Typography className={classes.priceLevelName}>
                    {level.name}
                  </Typography>
                  <Box className={classes.infoBox}>
                    <Box
                      className={classes.iconBox}
                      onMouseEnter={(e) => {
                        setAnchorEl(e.currentTarget);
                        setSelectedPriceLevel(level);
                      }}
                      onMouseLeave={() => {
                        setAnchorEl(null);
                        setSelectedPriceLevel(null);
                      }}
                    >
                      {showLinkIcon && (
                        <MiIcon
                          path={mdiLink}
                          size={0.8}
                          color={
                            level?.parentId
                              ? theme.palette.grey.main
                              : theme.palette.primary.main
                          }
                        />
                      )}
                      <MiIcon
                        path={mdiInformationSlabCircleOutline}
                        size={0.7}
                        color={theme.palette.grey.main}
                      />
                    </Box>
                    {!readOnly && (
                      <MiMenu
                        disabled={isPriceLevelAdded || openPriceLevelForm}
                        closeContextMenu={() => {}}
                        data={[
                          {
                            label: "Edit column",
                            icon: mdiPencilOutline,
                            action: () => handleEditDropPrices(level, idx),
                          },
                          {
                            label: "Duplicate column",
                            icon: mdiFileMultipleOutline,
                            action: () => handleDuplicatePriceLevel(level),
                          },
                          {
                            label: "Edit price level",
                            icon: mdiPencilOutline,
                            action: () => handleEditPriceLevel(level),
                          },
                          {
                            label: "Move left",
                            icon: mdiArrowLeft,
                            disabled: idx === 0,
                            action: () => handleReorderPriceLevels("left", idx),
                          },
                          {
                            label: "Move right",
                            icon: mdiArrowRight,
                            disabled: idx === priceLevels.length - 1,
                            action: () =>
                              handleReorderPriceLevels("right", idx),
                          },
                        ]}
                      />
                    )}
                  </Box>
                </Box>
                <ColumnHeaderActionButtons
                  open={isActive}
                  isDirty={isDirty}
                  handleSaveDropPrices={handleSaveDropPrices}
                  handleCancelPriceLevel={handleCancelPriceLevel}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
      <ScrollButtons
        isScrollable={priceLevels.length > maxVisibleLevels}
        visibleStartIndex={visibleStartIndex}
        lastScrollableIndex={lastScrollableIndex}
        setVisibleStartIndex={setVisibleStartIndex}
      />
      <PriceLevelInfo
        anchorEl={anchorEl}
        level={selectedPriceLevel}
        priceLevels={priceLevels}
      />
    </Box>
  );
}

const useStyles = makeStyles<StyleProps>()((theme, { transform }) => ({
  headerRow: {
    display: "flex",
    paddingLeft: theme.spacing(2),
  },
  priceLevelBox: {
    display: "flex",
    position: "relative",
  },
  priceLevelName: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: PRICE_LVL_COL_WIDTH,
    fontSize: theme.fontSize.medium,
  },
  infoBox: {
    display: "flex",
    alignItems: "center",
  },
  iconBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  // shared styles with CategoryDropRows
  dropNameCol: {
    width: DROP_NAME_COL_WIDTH,
    minWidth: DROP_NAME_COL_WIDTH,
    fontWeight: theme.fontWeight.semiBold,
    color: theme.palette.primary.main,
    paddingLeft: theme.spacing(2),
  },
  scrollWrapper: {
    flex: 1,
    overflow: "hidden",
    minWidth: 0,
  },
  scrollContent: {
    display: "flex",
    transition: "transform 0.3s ease",
    transform,
  },
  priceLevelCol: {
    width: PRICE_LVL_COL_WIDTH,
    minWidth: PRICE_LVL_COL_WIDTH,
    flexShrink: 0,
    backgroundColor: theme.palette.grey.mediumLight,
    display: "flex",
    alignItems: "center",
    padding: 8,
    borderRight: `1px solid ${theme.palette.divider}`,
    justifyContent: "space-between",
  },
  priceLevelColActive: {
    backgroundColor: `${theme.palette.primary.light}`,
  },
  recentlyModified: {
    backgroundColor: theme.palette.orange.light,
  },
}));

interface PriceLevelHeaderRowProps {
  form?: UseFormReturn<IPriceLevelPayload>;
  readOnly?: boolean;
  transform: string;
  priceLevels: IPriceLevelPayload[];
  maxVisibleLevels: number;
  visibleStartIndex: number;
  openPriceLevelForm: boolean;
  activePriceLevelIndex: number | null;
  setMaxVisibleLevels: React.Dispatch<React.SetStateAction<number>>;
  setVisibleStartIndex: React.Dispatch<React.SetStateAction<number>>;
  handleEditDropPrices: (level: IPriceLevelPayload, idx: number) => void;
  handleEditPriceLevel: (level: IPriceLevelPayload) => void;
  handleSaveDropPrices: () => void;
  handleCancelPriceLevel: () => void;
  handleReorderPriceLevels: (direction: "left" | "right", idx: number) => void;
  handleDuplicatePriceLevel: (level: IPriceLevelPayload) => void;
}

interface StyleProps {
  transform: string;
}
